<template>
  <div v-if="user" class="profile">
    <ProfileLoader v-if="profileLoading" />

    <div
      v-else
      class="profile__description"
      :style="{
        background: backgroundColor || 'var(--profile)',
        '--profile-bg': backgroundColor
          ? 'linear-gradient(86deg, #181818 0%, rgba(24, 24, 24, 0) 55.07%, #181818 100%)'
          : 'none',
        color: !backgroundColor ? 'var(--text-color)' : 'white',
      }"
    >
      <div class="profile__description_bg"></div>

      <div class="profile__user_info">
        <div class="profile__avatar">
          <Avatar :user="user" @click="openAvatar" />
        </div>

        <div class="profile__info">
          <div class="profile__info_text">
            <h2 v-if="user.name" class="profile__name">{{ user.name }}</h2>
            <span v-if="user.username" class="profile__username">{{
              "@" + user.username
            }}</span>

            <p class="followers">
              {{ user.followers_qty || 0 }} {{ $t("profile.followers") }} |
              {{ user.authors_qty || 0 }}
              {{ $t("profile.following") }}
            </p>
          </div>

          <div class="profile__button_box">
            <WhiteButton
              v-if="auth.user?.id === user?.id"
              @click="$router.push({ name: 'EditProfile' })"
            >
              {{ $t("profile.edit") }}
            </WhiteButton>

            <FollowBtn
              v-else
              :userId="user.id"
              :isFollowing="user.i_follow_him"
              @follow-change="onFollowChange"
            />
          </div>
        </div>
      </div>
      <div class="profile__description_bottom">
        <div
          v-if="user.about_me"
          :class="`edit_profile_bio_box bubble_profile ${user.metadata.bubble_type || 'default'}`"
        >
          <img
            v-if="user.about_me?.length > 5"
            :src="`/bubble/${user.metadata.bubble_type || 'default'}-tail.svg`"
            class="edit_profile_bio__tail"
            loading="lazy"
          />
          <p class="edit_profile__bio">{{ user.about_me }}</p>
        </div>
        <div v-else></div>

        <ProfileTegs :user="this.user" />
      </div>
    </div>

    <Loader v-if="postLoading" class="post_loader" />

    <div
      v-else-if="!postLoading && posts.length !== 0"
      class="profile__post_list"
    >
      <div
        v-if="posts.length"
        v-for="post in posts"
        :key="post.id"
        class="profile__post"
        @click="this.openPost(post.id)"
      >
        <img :src="postPreview(post)" alt="" loading="lazy" />
        <Bubble
          :image="post.metadata?.frames?.[0]?.bubbles_image_url || ''"
          :trigger="undefined"
        />
      </div>
    </div>

    <div v-else class="post__message">
      <img src="/LogoIcon.svg" alt="Logo" />
      <h1>{{ $t("profile.strip") }}</h1>
      <p>{{ $t("profile.emptyProfile") }}</p>
    </div>
  </div>
</template>

<script>
import Bubble from "@/UI/Bubble.vue";
import { useAuthStore } from "@/stores/authStore.js";
import { getPostsProfile, getUser } from "@/api/axios.js";
import { useOpenPost } from "@/composables/useOpenPost.js";
import FollowBtn from "@/UI/Button/FollowBtn.vue";
import ProfileLoader from "@/UI/Loaders/ProfileLoader.vue";
import Loader from "@/UI/Loaders/Loader.vue";
import ProfileTegs from "./ProfileTegs.vue";
import Avatar from "../Avatar.vue";
import { useModalStore } from "@/stores/modalStore";
import AvatarFullScreen from "./AvatarFullScreen.vue";
import WhiteButton from "@/UI/Button/WhiteButton.vue";

export default {
  name: "Profile",
  props: {
    id: {
      type: [String, Number],
      required: false,
    },
  },
  components: {
    Bubble,
    FollowBtn,
    ProfileLoader,
    Loader,
    ProfileTegs,
    Avatar,
    WhiteButton,
  },
  data() {
    return {
      auth: useAuthStore(),
      user: null,
      posts: [],
      hasMore: true,
      openPost: useOpenPost(this.posts).openPost,
      profileLoading: false,
      postLoading: false,
      modalStore: useModalStore(),
    };
  },
  computed: {
    backgroundColor() {
      if (!this.user || !this.user?.metadata?.firstBackground_color) {
        return false;
      }
      return `linear-gradient(225deg, ${this.user.metadata?.firstBackground_color} 50%, ${this.user.metadata?.secondBackground_color} 50%)`;
    },
  },
  methods: {
    onFollowChange(delta) {
      if (!this.user) return;

      this.user.followers_qty = (this.user.followers_qty || 0) + delta;

      this.user.i_follow_him = delta > 0;
    },
    async loadProfile() {
      this.profileLoading = true;
      try {
        if (this.id && this.id != this.auth.user?.id) {
          // чужой профиль
          const res = await getUser(this.id);
          this.user = res.data;
        } else {
          // свой профиль
          if (!this.auth.user) {
            await this.auth.fetchUser();
          }
          this.user = this.auth.user;
          this.profileLoading = false;
        }
        await this.loadPosts();
      } catch (err) {
        console.error("Ошибка загрузки профиля:", err);
      } finally {
        this.profileLoading = false;
      }
    },
    async loadPosts() {
      if (this.postLoading || !this.user?.id) return;
      this.postLoading = true;
      try {
        const res = await getPostsProfile(this.user.id);
        this.posts = res.data.results;
        if (!res.data.next) this.hasMore = false;
      } catch (err) {
        console.error("Ошибка загрузки постов:", err);
      } finally {
        this.postLoading = false;
      }
    },
    postPreview(post) {
      return post?.picture_1?.related
        ? import.meta.env.VITE_IMG_BASE_URL + post.picture_1.related
        : "";
    },
    openAvatar() {
      if (!this.user.userpic) return;

      this.modalStore.openModal({
        component: AvatarFullScreen,
        options: { lockScroll: true },
        props: { user: this.user },
      });
    },
  },
  async mounted() {
    await this.loadProfile();

    if (this.$route.name === "ProfilePost" && this.$route.params.postId) {
      this.openPost(this.$route.params.postId);
    }
  },
  watch: {
    id: {
      immediate: false,
      handler() {
        this.loadProfile();
      },
    },
  },
};
</script>

<style>
.profile {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  width: 100%;
}

.profile__description {
  width: 100%;
  margin-bottom: 20px;
  position: relative;
  background: var(--edit-profile-form);
  overflow: hidden;
  border-radius: 20px;
}

.profile__description_bg {
  position: absolute;
  overflow: hidden;
  margin-bottom: 24px;
  width: 100%;
  height: 100%;
  backdrop-filter: blur(200px);
  z-index: 0;
}

.profile__description::before {
  content: "";
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 0;
  background: var(--profile-bg);
}

.profile__description_header {
  padding: 16px;
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  z-index: 1;
}

.profile__description_bottom {
  display: flex;
  justify-content: space-between;
  box-sizing: border-box;
  width: 100%;
  align-items: flex-start;
  position: relative;
  z-index: 1;
  padding: 0 16px 16px 16px;
}

.profile__description_bubble {
  width: 60%;
}

.profile__description_bubble p {
  background-color: #ffffff;
  display: flex;
  align-items: center;
  padding: 12px 16px;
  border-radius: 20px;
  width: fit-content;
  max-width: 100%;
  font-size: 14px;
  line-height: 20px;
  color: #181818;
  font-weight: 500;
  height: 100%;
}

.profile__description_bubble img {
  position: absolute;
  width: 30px;
  height: 30px;
  top: -20px;
  left: 47px;
}

.profile__user_info {
  display: flex;
  z-index: 1;
  padding: 16px 16px 0 16px;
}

.profile__avatar {
  z-index: 1;
  width: 98px;
  height: 98px;
  margin-right: 20px;
  cursor: pointer;
}

.profile__avatar img {
  border-radius: 50%;
  width: 98px;
  height: 98px;
  overflow: hidden;
}

.profile__info {
  display: flex;
  justify-content: space-between;
  width: 100%;
  z-index: 1;
}

.profile__info_text {
  text-align: left;
  display: flex;
  flex-direction: column;
  width: 70%;
}

.profile__name {
  margin-bottom: 4px;
}

.profile__username {
  width: fit-content;
  padding: 5px 12px;
  border-radius: 10px;
  background-color: #8a8a8a46;
  font-weight: 700;
  font-size: 14px;
  text-align: center;
}

.profile__button_box {
  display: flex;
  align-items: flex-end;
  flex-direction: column;
  justify-content: space-between;
}

.profile__btn {
  border-radius: 12px;
  background: #fff;
  font-weight: 600;
  height: 32px;
  border: none;
  padding: 0 16px;
  cursor: pointer;
  transition: 0.2s all;
  width: fit-content;
}

.profile__btn:hover {
  background-color: #dddddd;
}

.profile__post_list {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(250px, max-content));
  justify-content: flex-start;
  gap: 4px;
  overflow: hidden;
  border-radius: 20px;
  position: relative;
  overflow: hidden;
  max-width: 100%;
  width: 100%;
}

.profile__post {
  background: rgba(255, 255, 255, 0.8);
  aspect-ratio: 3 / 4;
  cursor: pointer;
  transition: 0.2s all;
  object-fit: cover;
  display: block;
  position: relative;
  width: 100%;
  max-width: 350px;
}

.profile__post img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
  position: relative;
}

.profile__post:hover {
  opacity: 50%;
}

.profile__loading {
  text-align: center;
  margin-top: 40px;
  color: #888;
}

.followers {
  margin-top: 8px;
}

.post__message {
  color: var(--text-color);
  text-align: center;
  margin: 0 auto;
  margin-top: 80px;
  margin-bottom: 80px;
}

.post__message img {
  width: 80px;
  height: 80px;
}

.post_loader {
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
  height: 400px;
}

@media (max-width: 500px) {
  .followers {
    font-size: 13px;
  }

  .profile__btn {
    font-size: 12px;
  }

  .profile__name {
    font-size: 19px;
  }

  .profile__post {
    max-width: 100%;
  }
}
</style>
